/**
 * AmbientSignalField.jsx — GRANULLO_AMBIENT_FIELD_DEPTH_V3
 *
 * Background sensing layer with LOCALIZED STRUCTURAL PRESSURE AWARENESS.
 *
 * FIELD DEPTH LAW:
 * - The field reacts to semantic structural pressure, not cursor attraction
 * - Positive proof zones: local thread relaxation (taller, softer, spread)
 * - Negative proof zones: local compression + darkening
 * - Friction zones: micro turbulence (rapid subtle oscillation)
 * - Mouse proximity: structural attention (not toy attraction)
 * - Viewport entry: gentle awakening via IntersectionObserver
 *
 * Constraints: invisible until felt, zero scroll jank, zero performance drag,
 * no motion wallpaper behavior. Premium sensing infrastructure.
 *
 * MODE MODULATION: default, sensing, calm, alert — lerp interpolated.
 * Respects prefers-reduced-motion.
 */
import React, { useRef, useEffect, useMemo, useState, useCallback } from 'react';

const EDITORIAL_RED = '#CD2200';

const MODE_CONFIG = {
  default: { speedMul: 1.0, opacityMul: 1.0 },
  sensing: { speedMul: 1.3, opacityMul: 1.15 },
  calm:    { speedMul: 0.5, opacityMul: 0.7 },
  alert:   { speedMul: 1.6, opacityMul: 1.3 },
};

const LERP_RATE = 0.018;
const DEPTH_LERP = 0.025;
const INFLUENCE_RADIUS = 0.18; // Normalized (0-1) — how far a zone's influence extends

/* Gaussian-like falloff: 1 at center, ~0 at radius edge */
function fieldInfluence(dist, radius) {
  if (dist >= radius) return 0;
  const t = dist / radius;
  return Math.exp(-3.5 * t * t);
}

export function AmbientSignalField({
  lineCount = 24,
  height = 200,
  opacity = 0.12,
  color = EDITORIAL_RED,
  mode = 'default',
  className = '',
  semanticZones = null,
}) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const animRef = useRef(null);
  const timeRef = useRef(0);
  const speedMulRef = useRef((MODE_CONFIG[mode] || MODE_CONFIG.default).speedMul);
  const opacityMulRef = useRef((MODE_CONFIG[mode] || MODE_CONFIG.default).opacityMul);
  const targetModeRef = useRef(mode);
  const [reducedMotion, setReducedMotion] = useState(false);

  // Field Depth state (lerped, never snapped)
  const viewportIntensityRef = useRef(0);
  const viewportTargetRef = useRef(0);
  const mouseRelRef = useRef({ x: -1, y: -1 }); // -1 = out of field
  const mouseInfluenceRef = useRef(new Float32Array(lineCount)); // Per-line mouse influence (lerped)
  const zoneInfluenceRef = useRef(new Float32Array(lineCount)); // Per-line zone influence (positive=relax, negative=compress)
  const zoneTurbulenceRef = useRef(new Float32Array(lineCount)); // Per-line friction turbulence

  const hasDepth = semanticZones && semanticZones.length > 0;

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mq.matches);
    const handler = (e) => setReducedMotion(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    targetModeRef.current = mode;
  }, [mode]);

  // Resize mouse influence buffers when lineCount changes
  useEffect(() => {
    mouseInfluenceRef.current = new Float32Array(lineCount);
    zoneInfluenceRef.current = new Float32Array(lineCount);
    zoneTurbulenceRef.current = new Float32Array(lineCount);
  }, [lineCount]);

  // IntersectionObserver for viewport awakening
  useEffect(() => {
    if (!hasDepth) return;
    const el = containerRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        viewportTargetRef.current = entry.isIntersecting ? 1 : 0;
      },
      { threshold: [0, 0.15, 0.5] }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasDepth]);

  // Passive mouse tracking for structural pressure sensing
  useEffect(() => {
    if (!hasDepth) return;
    const el = containerRef.current;
    if (!el) return;

    let ticking = false;
    const onMove = (e) => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const rect = el.getBoundingClientRect();
        mouseRelRef.current = {
          x: (e.clientX - rect.left) / rect.width,
          y: (e.clientY - rect.top) / rect.height,
        };
        ticking = false;
      });
    };
    const onLeave = () => {
      mouseRelRef.current = { x: -1, y: -1 };
    };

    // Listen on document for proximity sensing beyond field bounds
    document.addEventListener('mousemove', onMove, { passive: true });
    el.addEventListener('mouseleave', onLeave, { passive: true });
    return () => {
      document.removeEventListener('mousemove', onMove);
      el.removeEventListener('mouseleave', onLeave);
    };
  }, [hasDepth]);

  const lines = useMemo(() => {
    return Array.from({ length: lineCount }, (_, i) => ({
      x: (i / (lineCount - 1)) * 100,
      baseHeight: 30 + Math.random() * 40,
      phase: Math.random() * Math.PI * 2,
      speed: 0.3 + Math.random() * 0.4,
      nodeY: 0.3 + Math.random() * 0.4,
      hasNode: Math.random() > 0.5,
      pulsePhase: Math.random() * Math.PI * 2,
    }));
  }, [lineCount]);

  // Pre-compute per-line semantic zone influence (static, only changes with semanticZones)
  const staticZoneMap = useMemo(() => {
    if (!hasDepth) return null;
    const influence = new Float32Array(lineCount);
    const turbulence = new Float32Array(lineCount);
    for (let i = 0; i < lineCount; i++) {
      const lineX = i / (lineCount - 1);
      let netInfluence = 0;
      let netTurbulence = 0;
      for (const zone of semanticZones) {
        const dist = Math.abs(lineX - zone.relativeX);
        const inf = fieldInfluence(dist, INFLUENCE_RADIUS) * (zone.intensity || 0.5);
        if (zone.type === 'positive') netInfluence += inf;
        else if (zone.type === 'negative') netInfluence -= inf;
        if (zone.type === 'friction') netTurbulence += inf;
      }
      influence[i] = netInfluence;
      turbulence[i] = netTurbulence;
    }
    return { influence, turbulence };
  }, [hasDepth, lineCount, semanticZones]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;

    const resize = () => {
      const rect = canvas.parentElement?.getBoundingClientRect();
      if (!rect) return;
      canvas.width = rect.width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = rect.width + 'px';
      canvas.style.height = height + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();

    const renderFrame = (t) => {
      const target = MODE_CONFIG[targetModeRef.current] || MODE_CONFIG.default;
      speedMulRef.current += (target.speedMul - speedMulRef.current) * LERP_RATE;
      opacityMulRef.current += (target.opacityMul - opacityMulRef.current) * LERP_RATE;

      // Lerp viewport intensity for smooth awakening
      if (hasDepth) {
        viewportIntensityRef.current += (viewportTargetRef.current - viewportIntensityRef.current) * DEPTH_LERP;
      }
      const vpI = hasDepth ? viewportIntensityRef.current : 1;

      const spdMul = speedMulRef.current;
      const effOpacity = opacity * opacityMulRef.current;
      const w = canvas.width / dpr;
      const h = height;
      ctx.clearRect(0, 0, w, h);

      // Pre-compute per-line mouse influence (lerped for smoothness)
      if (hasDepth) {
        const mx = mouseRelRef.current.x;
        const mi = mouseInfluenceRef.current;
        for (let i = 0; i < lineCount; i++) {
          const lineX = i / (lineCount - 1);
          const targetMi = mx >= 0 ? fieldInfluence(Math.abs(lineX - mx), 0.12) * 0.35 : 0;
          mi[i] += (targetMi - mi[i]) * 0.06; // Slow lerp — sensing, not chasing
        }
      }

      lines.forEach((line, idx) => {
        const x = (line.x / 100) * w;

        // Base height with mode modulation
        let heightVar = line.baseHeight + Math.sin(t * line.speed * spdMul + line.phase) * 15;

        // FIELD DEPTH: localized semantic influence
        let localOpacityMod = 1;
        let localLineWidthMod = 0;
        if (hasDepth && staticZoneMap) {
          const zi = staticZoneMap.influence[idx];
          const zt = staticZoneMap.turbulence[idx];
          const mi = mouseInfluenceRef.current[idx];

          // Semantic zone influence (positive=relax/taller, negative=compress/shorter)
          heightVar += zi * 18 * vpI; // ±18px max from semantic zones
          localOpacityMod += zi * -0.25 * vpI; // Positive → softer, Negative → darker

          // Friction turbulence: rapid micro-oscillation
          if (zt > 0.01) {
            heightVar += Math.sin(t * 8.5 + idx * 1.7) * zt * 6 * vpI;
          }

          // Mouse structural attention: local density increase
          if (mi > 0.01) {
            heightVar += mi * 12 * vpI;
            localOpacityMod += mi * 0.3 * vpI;
            localLineWidthMod += mi * 0.4 * vpI;
          }
        }

        const heightPx = (heightVar / 100) * h;
        const startY = h - heightPx;

        ctx.beginPath();
        ctx.moveTo(x, h);
        ctx.lineTo(x, startY);
        ctx.strokeStyle = color;
        ctx.globalAlpha = Math.min(1, effOpacity * (0.5 + 0.5 * Math.sin(t * 0.5 + line.phase)) * localOpacityMod);
        ctx.lineWidth = 0.8 + localLineWidthMod;
        ctx.stroke();

        if (line.hasNode) {
          const nodeY = startY + heightPx * line.nodeY;
          const pulseR = 1.5 + Math.sin(t * 0.8 * spdMul + line.pulsePhase) * 0.8;
          ctx.beginPath();
          ctx.arc(x, nodeY, pulseR, 0, Math.PI * 2);
          ctx.fillStyle = color;
          ctx.globalAlpha = Math.min(1, effOpacity * (0.6 + 0.4 * Math.sin(t * 1.2 * spdMul + line.pulsePhase)) * localOpacityMod);
          ctx.fill();
        }

        ctx.globalAlpha = 1;
      });
    };

    if (reducedMotion) {
      renderFrame(0);
      window.addEventListener('resize', resize);
      return () => window.removeEventListener('resize', resize);
    }

    const draw = () => {
      timeRef.current += 0.008;
      renderFrame(timeRef.current);
      animRef.current = requestAnimationFrame(draw);
    };

    draw();
    window.addEventListener('resize', resize);

    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener('resize', resize);
    };
  }, [lines, height, opacity, color, reducedMotion, hasDepth, staticZoneMap, lineCount]);

  return (
    <div ref={containerRef} className={`absolute inset-0 pointer-events-none overflow-hidden ${className}`} style={{ zIndex: 0 }} data-testid="ambient-signal-field">
      <canvas ref={canvasRef} className="absolute bottom-0 left-0 w-full" style={{ height }} />
    </div>
  );
}

/**
 * AmbientNodePulse — Single pulsing node for inline use
 */
export function AmbientNodePulse({ size = 4, color = EDITORIAL_RED, delay = 0 }) {
  return (
    <span className="inline-block relative" style={{ width: size * 3, height: size * 3 }}>
      <span
        className="absolute rounded-full"
        style={{
          width: size,
          height: size,
          background: color,
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          animation: `ambientPulse 2.4s ease-in-out ${delay}s infinite`,
        }}
      />
      <style>{`
        @keyframes ambientPulse {
          0%, 100% { opacity: 0.4; transform: translate(-50%, -50%) scale(1); }
          50% { opacity: 0.8; transform: translate(-50%, -50%) scale(1.6); }
        }
      `}</style>
    </span>
  );
}

/**
 * SignatureExpansionGlyph — Re-exported from canonical EditorialPrimitives.
 * This re-export maintains backward compatibility for all existing imports.
 */
export { SignatureExpansionGlyph, EditorialSectionNumber, EditorialCircledClose } from './EditorialPrimitives';
